import { useEffect } from 'react';
import type { ApolloClient } from '@apollo/client';

/**
 * Refetches all active (currently mounted) Apollo queries when the app returns to
 * the foreground, covering the two distinct "came back to the page" signals:
 *
 * - `visibilitychange` → visible: the tab/webview regains focus, e.g. returning
 *   from a `target="_blank"` link or switching back from another app. The page
 *   never unloaded, so `pageshow` does NOT fire here. (IAFE-2525)
 * - `pageshow` with `persisted=true`: the page is restored from the back/forward
 *   cache after a same-tab navigation + browser Back. This is the canonical bfcache
 *   signal and — unlike `visibilitychange` — fires reliably across browsers and
 *   native webviews, so it is kept rather than relying on visibility alone.
 *   (IAFE-1455)
 *
 * Why both: neither event covers the other. `pageshow`/`persisted` never fires for
 * the new-tab case, and `visibilitychange` is not a guaranteed signal for bfcache
 * restoration (especially in native webviews).
 *
 * Since a single bfcache restore can fire both return events, the hook marks a
 * refetch as pending when the page is left (`visibilitychange` → hidden /
 * `pagehide`) and performs it at most once on the next return, clearing the flag.
 * This dedupes the paired events without depending on their ordering or a timing
 * heuristic: exactly one refetch per leave→return cycle.
 *
 * Auto Apply shares job-seeker data (SQ/DQ prefill preferences, resume selection,
 * match preferences, drafts, ...) with other surfaces — the Profile site, the
 * native app, other tabs — any of which can change it while an Auto Apply page
 * stays mounted with stale `cache-first` data.
 *
 * Called at the App root (above `ApolloProvider`) so it covers every route,
 * including layout-less ones such as post-apply; the client is passed in explicitly
 * because there is no provider above the call site.
 *
 * Accepted trade-offs: active queries briefly re-enter their loading state on
 * return, and any unsaved selection written into the cache before submit (e.g. the
 * automation-level picker) is re-seeded from the server.
 */
export const useRefetchOnPageResume = <TCacheShape>(client: ApolloClient<TCacheShape>): void => {
    useEffect(() => {
        // Set when the page is backgrounded/frozen; a single restore can fire both
        // return events, so we refetch once on the next return and clear the flag.
        let refetchPending = false;

        const markRefetchPending = () => {
            refetchPending = true;
        };
        const refetchIfPending = () => {
            if (!refetchPending) {
                return;
            }
            refetchPending = false;
            // `include: 'active'` targets only mounted queries; unmounted routes
            // refetch naturally on their next mount.
            void client.refetchQueries({ include: 'active' });
        };

        const handleVisibilityChange = () => {
            // DocumentVisibilityState is only 'hidden' | 'visible': leaving marks a
            // refetch pending, the return (visible) performs it.
            if (document.visibilityState === 'hidden') {
                markRefetchPending();
            } else {
                refetchIfPending();
            }
        };
        const handlePageHide = () => {
            markRefetchPending();
        };
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                refetchIfPending();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('pageshow', handlePageShow);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [client]);
};
